// FT-200 (REQ-07, REQ-11): общее для кабинета клиента и «набора возврата» — деньги ответа и отчёт о сбое запроса без пары.
//
// Модуль — часть «набора возврата» (Task 6, implementation-plan → «Совместимость версий и откат»): страница возврата
// из банка (ClientResult) и вход (ClientLogin) живут и после отката vue к старому кабинету, поэтому не импортируют
// clientCabinetView.js — модуль редизайна, который откат удалит. clientCabinetView реэкспортирует отсюда для блоков
// кабинета. Без Vue, стора, роутера и других модулей приложения.
//
// Деньги нового ответа кабинета: начисления бронирования — числа, суммы платежей провайдеров и to_pay — строки
// десятичной записи ("3000.0").

// Окно, в котором свежая привязка карты без исхода считается «банк ещё проверяет»: блок карты кабинета
// (clientCabinetView.cardBindingPending) и страница возврата (ClientResult — свежая привязка без карты у клиента).
// Брошенная привязка (клиент закрыл форму банка) остаётся без исхода навсегда, поэтому окно ограничено; клиент
// возвращается из банка не сразу (ввод карты, 3-D Secure).
export const CARD_CHECK_WINDOW_MS = 30 * 60 * 1000;

export function parseAmount(value) {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "number") return value;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return NaN;
  return Number(normalized);
}

// Все вычисления денег проходят через целые копейки. Строковый разбор не наследует двоичную ошибку IEEE-754
// (`0.10 + 0.20`) и корректно округляет половину копейки (`1.005` → `1.01`).
export function moneyCents(value) {
  if (value === null || value === undefined) return NaN;
  let normalized;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return NaN;
    normalized = value.toFixed(10).replace(/0+$/, "").replace(/\.$/, "");
  } else {
    normalized = String(value).replace(/\s/g, "").replace(",", ".");
  }
  const match = normalized.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return NaN;
  const whole = Number(match[2]);
  if (!Number.isSafeInteger(whole) || whole > Math.floor(Number.MAX_SAFE_INTEGER / 100)) return NaN;
  const fraction = match[3] || "";
  let cents = whole * 100 + Number(`${fraction}00`.slice(0, 2));
  if (Number(fraction[2] || 0) >= 5) cents += 1;
  return match[1] ? -cents : cents;
}

export function roundMoney(value) {
  const cents = moneyCents(value);
  return Number.isNaN(cents) ? NaN : cents / 100;
}

// «3 000», «1 500.50»: целое — без копеек, дробное — две цифры; обычный пробел не зависит от локали браузера.
export function formatAmount(value) {
  const amount = parseAmount(value);
  if (Number.isNaN(amount)) return "";
  const rounded = roundMoney(value);
  const fixed = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  const [integer, fraction] = fixed.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
}

export function money(value, symbol) {
  const amount = formatAmount(value);
  if (!amount) return "";
  return symbol ? `${amount} ${symbol}` : amount;
}

// ─── Отчёт об ошибке запроса ────────────────────────────────────────────────

// Копия ошибки axios для отчёта: метод, адрес, статус и код — без тела запроса и ответа. В config.data запросов
// кабинета лежит пара номер + фамилия; console.log и Rollbar ошибки целиком унесли бы её в логи (REQ-07).
export function redactedRequestError(error) {
  const source = error || {};
  const safe = new Error(source.message || "Request failed");
  safe.name = source.name || "Error";
  if (source.code) safe.code = source.code;
  if (source.isAxiosError) safe.isAxiosError = true;
  if (source.response) safe.response = { status: source.response.status, statusText: source.response.statusText };
  if (source.config) safe.config = { method: source.config.method, url: source.config.url, timeout: source.config.timeout };
  return safe;
}

// Сбой на стороне сервера (5xx) — в отчёт об ошибках, без тела запроса. Отказы 4xx и обрыв связи — не сбой кабинета.
export function reportServerError(vm, text, error) {
  const status = error && error.response && error.response.status;
  if (!(status >= 500) || typeof vm.$reportError !== "function") return;
  vm.$reportError(text, redactedRequestError(error));
}
